/**
 * RISK-IMPORT-MAPPING
 * Select custom attributs to import
 */

import ConfirmButtons from "@/components/confirm-buttons"
import { createEmptyRisk, Risk, RISK_MAPPING_COLUMNS, RiskMapping } from "../model/risk.types"
import ListItem from "@/components/list-item"
import Dropdown from "@/components/dropdown"
import React, { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCirclePlus, faDatabase, faTag, faTimesCircle } from "@fortawesome/free-solid-svg-icons"
import { t } from "i18next"
import ListButton from "@/components/list-button"
import { compact } from "lodash"
import TextInput from "@/components/text-input"
import { createEmptyUnit, Unit } from "../../unit/model/unit.types"
import { toast } from "react-toastify"
interface Props {
  onConfirm: Function
  mapping: Partial<RiskMapping>
}

export function RisksImportMapping(props: Props) {
  const [importColumns, setImportColumns] = useState(() => {
    const cols = props.mapping.importColumns ?? []
    const standard = cols.filter((c) => !c.isCustom)
    const custom = cols.filter((c) => c.isCustom)
    return [...standard, ...custom]
  })

  //Convert value to boolean for RPS field
  function convertRpsValue(value: any): boolean {
    if (value === null || value === undefined) return false
    if (typeof value === "boolean") return value
    if (typeof value === "string") {
      const lower = value.toLowerCase().trim()
      return lower === "true" || lower === "1" || lower === "yes" || lower === "oui"
    }
    if (typeof value === "number") return value === 1
    return false
  }

  //Convert value to Date for dateValidated field
  function convertDateValue(value: any): Date | null {
    if (value === null || value === undefined || value === "") return null
    if (value instanceof Date) return value
    if (typeof value === "string") {
      const trimmed = value.trim()
      if (!trimmed) return null
      // Try DD/MM/YYYY format FIRST (French format)
      const parts = trimmed.split("/")
      if (parts.length === 3) {
        const d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`)
        if (!isNaN(d.getTime())) return d
      }
      // Try parsing with ISO or common formats as fallback
      const date = new Date(trimmed)
      if (!isNaN(date.getTime())) return date
    }
    if (typeof value === "number") {
      // Check if it's an Excel date number (between 25000 and 60000)
      if (value > 25000 && value < 60000) {
        // Excel date: 25569 = days between 1900-01-01 and 1970-01-01
        const utc_days = Math.floor(value - 25569)
        const utc_value = utc_days * 86400
        const date = new Date(utc_value * 1000)
        if (!isNaN(date.getTime())) return date
      }
      // Try as milliseconds (only for large timestamps)
      if (value > 1000000000) {
        const date = new Date(value)
        if (!isNaN(date.getTime())) return date
      }
    }
    return null
  }

  // Decode HTML entities from Excel
  function decodeHtmlEntities(text: string): string {
    const textarea = document.createElement("textarea")
    textarea.innerHTML = text
    return textarea.value
  }

  //Set custom value
  function addColumn(columnName: string) {
    setImportColumns((prev) =>
      prev.map((col) => {
        if (col.name === columnName) {
          return { ...col, isCustom: true, mapping: columnName }
        }
        return col
      })
    )
  }

  //Confirm value and update store
  async function confirm() {
    if (props.mapping.rows) {
      const header = props.mapping.rows[0]
      const data = props.mapping.rows.slice(1)

      // Créer les units uniques
      const units: Unit[] = []
      const unitCol = importColumns.find((c) => c.mapping === "unitName")
      const unitDescriptionCol = importColumns.find((c) => c.mapping === "unitDescription")
      const unitSizeCol = importColumns.find((c) => c.mapping === "unitSize")

      if (!unitCol) {
        toast("La colonne Unité de Travail n'a pas été définie.", { type: "error" })
        return null
      }

      //Create units
      const unitIndex = header.indexOf(unitCol.name)
      for (const row of data) {
        const unitValue = decodeHtmlEntities(String(row[unitIndex] ?? ""))
        const exists = units.some((u) => u.name === unitValue)
        if (!exists) {
          let size: number | undefined = undefined
          let description: string | undefined = undefined

          //Search description values
          if (unitDescriptionCol) {
            const descriptions = compact(
              data
                .filter((x) => decodeHtmlEntities(String(x[unitIndex] ?? "")) === unitValue)
                .map((x) => {
                  const val = x[header.indexOf(unitDescriptionCol.name)]
                  return typeof val === "string" ? decodeHtmlEntities(val) : val
                })
            )
            if (descriptions.length) {
              description = descriptions[0]
            }
          }

          //Search size values
          if (unitSizeCol) {
            const sizes = data
              .filter((x) => x[unitIndex] === unitValue)
              .map((x) => parseInt(x[header.indexOf(unitSizeCol.name)], 10))
              .filter((n) => !isNaN(n)) // <-- on enlève les NaN

            if (sizes.length) {
              size = sizes[0]
            }
          }

          units.push(createEmptyUnit({ name: unitValue, size, description }))
        }
      }

      //Créer les risques
      const risks: Risk[] = []
      for (const row of data) {
        let unitId: string | null = null

        const unitCol = importColumns.find((c) => c.mapping === "unitName")
        if (unitCol) {
          const unitIndex = header.indexOf(unitCol.name)
          const unitValue = decodeHtmlEntities(String(row[unitIndex] ?? ""))
          const unit = units.find((u) => u.name === unitValue)
          if (unit) {
            unitId = unit.id
          }
        }

        // squelette risk
        const risk = createEmptyRisk({ UnitId: unitId })

        // parcourir toutes les colonnes
        for (const col of importColumns) {
          let value = row[header.indexOf(col.name)]
          // Decode HTML entities
          if (typeof value === "string") {
            value = decodeHtmlEntities(value)
          }
          if (col.isCustom && col.mapping) {
            risk.attributes[col.mapping] = value ?? ""
          } else if (col.mapping) {
            // Convert RPS to boolean
            if (col.mapping === "rps") {
              risk[col.mapping] = convertRpsValue(value)
            } else if (col.mapping === "dateValidated") {
              // Convert to Date
              risk[col.mapping] = convertDateValue(value)
            } else {
              risk[col.mapping] = value ?? null
            }
          }
        }

        risks.push(risk)
      }

      //Confirm
      props.onConfirm({
        rows: props.mapping.rows,
        importColumns,
        risks,
        units
      })
    } else {
      toast("Le classeur ne contient pas de données", { type: "error" })
    }
  }

  //Edit value for the column
  function editColumn(columnName: string, value: string) {
    setImportColumns((prev) =>
      prev.map((col) => {
        if (col.name === columnName) {
          return { ...col, mapping: value }
        }
        return col
      })
    )
  }

  //Update description
  function getDescription(mapping: string | null) {
    if (mapping) {
      const currentMapping = getMapping(mapping)
      if (currentMapping) {
        return <div style={{ fontSize: 12 }}>{t(currentMapping.description)}</div>
      } else {
        return null
      }
    } else {
      return null
    }
  }

  //Find correspondant
  function getMapping(mapping: string) {
    return RISK_MAPPING_COLUMNS.find((x) => x.id === mapping)
  }

  //Label
  function getLabel(mapping: string | null) {
    if (mapping) {
      const currentMapping = getMapping(mapping)
      return currentMapping ? t(currentMapping.text) : mapping
    } else {
      return null
    }
  }

  //Set column
  function setColumn(columnName: string, mappingName: string | null) {
    setImportColumns((prev) =>
      prev.map((col) => {
        if (col.name === columnName) {
          // la colonne ciblée → on lui met le nouveau mapping
          return { ...col, mapping: mappingName, isCustom: false }
        }
        if (mappingName && col.mapping === mappingName) {
          // une autre colonne avait déjà ce mapping → on le libère
          return { ...col, mapping: null, isCustom: false }
        }
        return col
      })
    )
  }

  return (
    <div>
      <ListItem>
        <div className="flex1">
          <b>Colonnes à importer</b>
        </div>
        <div className="w-20" />
        <div style={{ width: 258 }}>
          <b>Colonne dans IDTree</b>
        </div>
        <div style={{ width: 152 }}>
          <b>Description</b>
        </div>
      </ListItem>
      {importColumns.map((importColumn, index) => {
        const prevColumn = importColumns[index - 1]
        const isFirstStandard = index === 0 && !importColumn.isCustom
        const isFirstCustom = importColumn.isCustom && (!prevColumn || !prevColumn.isCustom)
        return (
        <React.Fragment key={importColumn.name}>
          {isFirstStandard && (
            <ListItem>
              <div className="flex items-center gap-2 grey-t" style={{ fontSize: 12 }}>
                <FontAwesomeIcon icon={faDatabase} />
                <b>{t("Champs système")}</b>
              </div>
            </ListItem>
          )}
          {isFirstCustom && (
            <ListItem>
              <div className="flex items-center gap-2 grey-t" style={{ fontSize: 12 }}>
                <FontAwesomeIcon icon={faTag} />
                <b>{t("Attributs personnalisés")}</b>
              </div>
            </ListItem>
          )}
        <ListItem>
          <div className="flex1">
            <div>
              {importColumn.name}
              <ul className="grey-t mt-[6px]" style={{ fontSize: 12, margin: 0, paddingLeft: 16 }}>
                {importColumn.sample.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="w-20" />
          <div className="flex" style={{ height: "fit-content" }}>
            {importColumn.isCustom ? (
              <div style={{ width: 210 }}>
                <TextInput
                  isFullWidth
                  value={importColumn.mapping}
                  onChange={(e) => editColumn(importColumn.name, e.value)}
                />
              </div>
            ) : (
              <Dropdown
                warning={!importColumn.mapping}
                list={RISK_MAPPING_COLUMNS.map(({ id, text }) => ({ id, text: t(text) }))}
                // onAdd={() => addColumn(importColumn.name)}
                displayField="text"
                onSelect={(e) => setColumn(importColumn.name, e.id)}
                value={getLabel(importColumn.mapping)}
              />
            )}

            <div style={{ width: 200 }} className="flex">
              <div style={{ marginTop: 17 }}>
                {!importColumn.mapping ? (
                  <ListButton
                    icon={faCirclePlus}
                    text="Ajouter une colonne dans IDTree"
                    isColorActive
                    isPrimary
                    onClick={() => addColumn(importColumn.name)}
                  />
                ) : (
                  <ListButton
                    icon={faTimesCircle}
                    text="Ne pas importer la colonne dans IDTree"
                    onClick={() => setColumn(importColumn.name, null)}
                  />
                )}
              </div>
              {getDescription(importColumn.mapping)}
            </div>
          </div>
        </ListItem>
        </React.Fragment>
      )})}
      <ConfirmButtons onConfirm={() => confirm()} />
    </div>
  )
}
